import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Card, Tag, Button, Tooltip } from 'antd';
import { GitBranch, Play, CheckCircle } from 'lucide-react';
import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';

interface ConditionNodeData {
  label: string;
  condition: string;
  description?: string;
  isExecuting?: boolean;
  isCompleted?: boolean;
  onDelete?: () => void;
  isUnlinked?: boolean;
}

const ConditionNode: React.FC<NodeProps<ConditionNodeData>> = ({ data, selected }) => {
  const { label, condition, description, isExecuting, isCompleted, onDelete, isUnlinked } = data;

  const getCardStyle = () => {
    if (isExecuting) {
      return {
        borderColor: '#13c2c2',
        boxShadow: '0 0 10px rgba(19, 194, 194, 0.5)',
        animation: 'pulse 1.5s ease-in-out infinite'
      };
    }
    if (isCompleted) {
      return {
        borderColor: '#52c41a',
        backgroundColor: '#f6ffed'
      };
    }
    if (selected) {
      return {
        borderColor: '#722ed1',
        boxShadow: '0 0 0 2px rgba(114, 46, 209, 0.2)'
      };
    }
    return {
      borderColor: '#d9d9d9'
    };
  };

  const getIcon = () => {
    if (isExecuting) return <Play className="h-4 w-4 text-cyan-600" />;
    if (isCompleted) return <CheckCircle className="h-4 w-4 text-green-600" />;
    return <GitBranch className="h-4 w-4 text-cyan-600" />;
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: '#13c2c2' }}
      />
      
      <Card
        size="small"
        style={{
          width: 200,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          position: 'relative',
          ...getCardStyle()
        }}
        className="condition-node"
      >
        {/* Caution Icon for Unlinked Nodes */}
        {isUnlinked && (
          <Tooltip title="This node is not connected to any other nodes">
            <WarningOutlined
              style={{
                position: 'absolute',
                top: '4px',
                left: '4px',
                color: '#faad14',
                fontSize: '14px',
                zIndex: 10,
                backgroundColor: 'white',
                borderRadius: '50%',
                padding: '2px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}
            />
          </Tooltip>
        )}
        
        {/* Delete Button */}
        {onDelete && (
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              width: '20px',
              height: '20px',
              fontSize: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              opacity: 0.7,
              transition: 'opacity 0.2s'
            }}
            className="node-delete-btn"
          />
        )}
        <div className="flex items-start space-x-2">
          <div className="flex-shrink-0">
            <div className={`p-2 rounded-lg ${isExecuting ? 'bg-cyan-100' : isCompleted ? 'bg-green-100' : 'bg-cyan-100'}`}>
              {getIcon()}
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-gray-900 truncate">
              {label}
            </h4>
            
            <div className="mt-1">
              <Tag color="cyan">
                condition
              </Tag>
            </div>
            
            <div className="mt-2 p-2 bg-gray-50 rounded text-xs font-mono text-gray-700">
              {condition}
            </div>
            
            {description && (
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                {description}
              </p>
            )}
            
            {isExecuting && (
              <div className="mt-2">
                <div className="flex items-center space-x-1">
                  <div className="animate-spin h-3 w-3 border-2 border-cyan-600 border-t-transparent rounded-full" />
                  <span className="text-xs text-cyan-600">Evaluating...</span>
                </div>
              </div>
            )}
            
            {isCompleted && (
              <div className="mt-2">
                <div className="flex items-center space-x-1">
                  <div className="h-3 w-3 bg-green-600 rounded-full" />
                  <span className="text-xs text-green-600">Evaluated</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
      
      {/* Multiple output handles for different conditions */}
      <Handle
        type="source"
        position={Position.Right}
        id="true"
        style={{ background: '#52c41a', top: '70%' }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="false"
        style={{ background: '#ff4d4f', top: '70%' }}
      />
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        .condition-node:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }

        .condition-node .node-delete-btn {
          opacity: 0;
          transition: opacity 0.2s;
        }

        .condition-node:hover .node-delete-btn {
          opacity: 1;
        }

        .node-delete-btn:hover {
          background-color: #ff4d4f !important;
          color: white !important;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default ConditionNode;